import { ShieldCheck } from "lucide-react";
import { Outlet } from "react-router-dom";
import { AppLogo } from "@/components/common/app-logo";
import { brand } from "@/config/brand";

const TRUST_POINTS = [
  "Encrypted at rest & in transit",
  "Two-step verification",
  "7-year audit log",
];

/**
 * Split-screen shell (Calm Studio "brand panel" design): a deep-navy branding
 * panel on the left with a soft blue/orange glow atmosphere, a live status
 * pill, hero copy and a trust row; auth forms render via Outlet on the right.
 * The branding panel collapses on mobile in favor of a compact logo above the
 * form. All decorative motion respects `prefers-reduced-motion`.
 */
export function AuthLayout() {
  return (
    <div className="bg-background flex h-screen w-full overflow-hidden">
      <div className="bg-brand-panel relative hidden w-2/5 flex-col justify-between overflow-hidden p-12 text-white md:flex">
        {/* Atmosphere: blue + orange radial glows and a faint dot grid. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 -left-40 size-[520px] rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(36,87,230,0.45), rgba(36,87,230,0))",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-44 -bottom-52 size-[560px] rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(242,140,40,0.38), rgba(242,140,40,0))",
          }}
        />

        <AppLogo className="relative z-10" />

        <div className="relative z-10 max-w-md">
          <span className="mb-5 inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold tracking-wide">
            <span className="size-2 animate-pulse rounded-full bg-[#4ade80] motion-reduce:animate-none" />
            Monitoring transcripts in real time
          </span>
          <h2 className="font-heading mb-4 text-4xl leading-tight font-semibold tracking-tight">
            Every IRS notice, deadline and client — <span className="text-primary">in one calm place.</span>
          </h2>
          <p className="text-brand-panel-muted text-lg">
            Transcripts, POAs, letters, invoices and a branded client portal, built for
            practitioners who resolve tax problems for a living.
          </p>
        </div>

        <div className="relative z-10 flex flex-col gap-4">
          <div className="text-brand-panel-muted flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium">
            {TRUST_POINTS.map((point) => (
              <span key={point} className="inline-flex items-center gap-1.5">
                <ShieldCheck className="size-3.5 text-[#4ade80]" />
                {point}
              </span>
            ))}
          </div>
          <div className="text-brand-panel-muted flex items-center gap-3 text-xs font-medium">
            <span>&copy; {new Date().getFullYear()} {brand.name}</span>
            <span>&bull;</span>
            <a href="#" className="transition-colors hover:text-white">
              Privacy
            </a>
            <span>&bull;</span>
            <a href="#" className="transition-colors hover:text-white">
              Terms
            </a>
          </div>
        </div>
      </div>

      <div className="bg-background flex h-full w-full flex-col overflow-y-auto md:w-3/5">
        <div className="mx-auto flex w-full max-w-[600px] flex-1 flex-col justify-center px-4 py-12 sm:px-8">
          <AppLogo variant="compact" className="mb-8 px-2 md:hidden" />
          <Outlet />
        </div>
      </div>
    </div>
  );
}
